import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import bakingApi from '../services/bakingApi';
import { providerJobCreationEnabled } from '../baking/executionProviders';

function NewBakePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [projectId, setProjectId] = useState(searchParams.get('project') || '');
  const [revisionId, setRevisionId] = useState('');
  const [sceneId, setSceneId] = useState('');
  const [profileId, setProfileId] = useState('');
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingDeps, setLoadingDeps] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [compatibility, setCompatibility] = useState(null);
  const [compatibilityLoading, setCompatibilityLoading] = useState(false);
  const [acknowledgeChecked, setAcknowledgeChecked] = useState(false);
  const [acknowledgementId, setAcknowledgementId] = useState('');
  const [acknowledging, setAcknowledging] = useState(false);

  useEffect(() => {
    (async () => {
      setLoadingMeta(true);
      setError(null);
      try {
        const data = await bakingApi.listProjects();
        const filtered = (data || []).filter((p) =>
          providerJobCreationEnabled(p.execution_provider || 'unity')
        );
        setProjects(filtered);
        const preferred = searchParams.get('project');
        if (preferred && filtered.some((p) => p.id === preferred)) {
          setProjectId(preferred);
        }
      } catch (err) {
        setError(err.message || 'Failed to load projects');
      } finally {
        setLoadingMeta(false);
      }
    })();
  }, [searchParams]);

  useEffect(() => {
    if (!projectId) {
      setRevisions([]);
      setScenes([]);
      setProfiles([]);
      setRevisionId('');
      setSceneId('');
      setProfileId('');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingDeps(true);
      setError(null);
      try {
        const [rev, scn, profilesData] = await Promise.all([
          bakingApi.listRevisions(projectId),
          bakingApi.listScenes(projectId),
          bakingApi.listBakeProfiles(projectId),
        ]);
        if (cancelled) return;
        setRevisions(rev);
        setScenes(scn);
        setProfiles(profilesData);
        setRevisionId('');
        setSceneId('');
        setProfileId('');
        setCompatibility(null);
        setAcknowledgeChecked(false);
        setAcknowledgementId('');
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load project details');
      } finally {
        if (!cancelled) setLoadingDeps(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!revisionId) {
      setCompatibility(null);
      setAcknowledgeChecked(false);
      setAcknowledgementId('');
      return;
    }
    let cancelled = false;
    (async () => {
      setCompatibilityLoading(true);
      setError(null);
      try {
        const data = await bakingApi.getRevisionCompatibility(revisionId);
        if (!cancelled) {
          setCompatibility(data);
          setAcknowledgeChecked(false);
          setAcknowledgementId('');
        }
      } catch (err) {
        if (!cancelled) {
          if (err.status === 404 && err.code === 'compatibility_analysis_missing') {
            setCompatibility(null);
          } else {
            setError(err.message || 'Failed to load compatibility');
          }
        }
      } finally {
        if (!cancelled) setCompatibilityLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [revisionId]);

  const readyRevisions = useMemo(
    () => revisions.filter((r) => r.status === 'ready'),
    [revisions]
  );

  const selectedProject = projects.find((p) => p.id === projectId);
  const compatibleProfiles = useMemo(() => {
    if (!selectedProject) return [];
    const provider = selectedProject.execution_provider || 'unity';
    return profiles.filter((p) => {
      if (!p.is_active) return false;
      if ((p.execution_provider || 'unity') !== provider) return false;
      if (provider === 'maya') {
        const required =
          selectedProject.application_version || p.application_version || '';
        if (!required) return true;
        return (
          !p.application_version || p.application_version === required
        );
      }
      return (
        p.unity_version === selectedProject.unity_version &&
        p.render_pipeline === selectedProject.render_pipeline &&
        p.render_pipeline_version === selectedProject.render_pipeline_version
      );
    });
  }, [profiles, selectedProject]);

  const compatibilityBlocked =
    !compatibility ||
    compatibility.is_stale ||
    compatibility.status === 'rejected' ||
    compatibility.status === 'analysis_failed' ||
    !compatibility.job_creation_allowed;

  const needsAcknowledgement =
    compatibility &&
    compatibility.status === 'conditionally_supported' &&
    compatibility.job_creation_allowed &&
    !compatibility.is_stale;

  const waivableFindings =
    (compatibility &&
      compatibility.findings &&
      compatibility.findings.filter((f) => f.acknowledgement_allowed)) ||
    [];

  const canSubmit =
    projectId &&
    revisionId &&
    sceneId &&
    profileId &&
    !submitting &&
    !loadingDeps &&
    !compatibilityLoading &&
    !compatibilityBlocked &&
    (!needsAcknowledgement || (acknowledgeChecked && acknowledgementId));

  const onAcknowledge = async () => {
    if (!needsAcknowledgement || !revisionId || !sceneId || !profileId) return;
    setAcknowledging(true);
    setError(null);
    try {
      const ack = await bakingApi.acknowledgeRevisionCompatibility(revisionId, {
        scene_id: sceneId,
        bake_profile_id: profileId,
        finding_codes: waivableFindings.map((f) => f.code),
      });
      setAcknowledgementId(ack.id);
      setAcknowledgeChecked(true);
    } catch (err) {
      setError(err.message || 'Acknowledgement failed');
      setAcknowledgeChecked(false);
      setAcknowledgementId('');
    } finally {
      setAcknowledging(false);
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const idempotencyKey =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `bake-${Date.now()}`;
      const job = await bakingApi.createJob(
        {
          project_id: projectId,
          revision_id: revisionId,
          scene_id: sceneId,
          bake_profile_id: profileId,
          ...(acknowledgementId ? { acknowledgement_id: acknowledgementId } : {}),
        },
        idempotencyKey
      );
      navigate(`/console/jobs/${job.id}`);
    } catch (err) {
      setError(err.message || 'Failed to create bake job');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h1 className="product-page-title">New Bake</h1>
      <p className="product-page-subtitle">
        Submit a job for a ready project revision. Execution requires a worker (not in this slice).
      </p>

      {loadingMeta && (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading…
        </div>
      )}

      {error && <div className="error-state mb-3">{error}</div>}

      {!loadingMeta && projects.length === 0 && (
        <div className="product-card empty-state">
          <p>Create a project before submitting a bake job.</p>
          <Button as={Link} to="/console/projects" variant="outline-primary">
            Go to projects
          </Button>
        </div>
      )}

      {!loadingMeta && projects.length > 0 && (
        <Form className="product-card" onSubmit={onSubmit}>
          <div className="form-stage">
            <Form.Label>1. Project</Form.Label>
            <Form.Select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              required
            >
              <option value="">Select a project…</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.unity_version} / {p.render_pipeline})
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="form-stage">
            <Form.Label>2. Ready revision</Form.Label>
            {loadingDeps ? (
              <div className="text-muted">Loading revisions…</div>
            ) : (
              <Form.Select
                value={revisionId}
                onChange={(e) => setRevisionId(e.target.value)}
                required
                disabled={!projectId}
              >
                <option value="">Select a ready revision…</option>
                {readyRevisions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.id.slice(0, 8)}… — {r.status}
                    {r.is_development_fixture ? ' (development fixture)' : ''}
                  </option>
                ))}
              </Form.Select>
            )}
            {projectId && !loadingDeps && readyRevisions.length === 0 && (
              <Alert variant="warning" className="mt-2 mb-0">
                No ready revisions. Archive upload is not implemented yet. An admin can create a
                labeled development fixture revision in Django admin.
              </Alert>
            )}
          </div>

          {revisionId && (
            <div className="form-stage">
              <Form.Label>Compatibility</Form.Label>
              {compatibilityLoading ? (
                <div className="text-muted">Loading compatibility…</div>
              ) : !compatibility ? (
                <Alert variant="warning" className="mb-0">
                  No compatibility analysis for this revision. Analyze it from the project page
                  before submitting a bake job.
                </Alert>
              ) : (
                <>
                  <Alert
                    variant={
                      compatibility.status === 'supported'
                        ? 'success'
                        : compatibility.status === 'conditionally_supported'
                          ? 'warning'
                          : 'danger'
                    }
                    className="mb-2"
                  >
                    <strong>{compatibility.status}</strong>
                    {compatibility.is_stale && ' · reanalysis required'}
                    {!compatibility.job_creation_allowed && ' · job creation blocked'}
                    {compatibility.summary ? ` — ${compatibility.summary}` : ''}
                  </Alert>
                  {compatibilityBlocked && (
                    <Alert variant="light" className="mb-2 border">
                      Submit is disabled until compatibility is supported and current.
                    </Alert>
                  )}
                  {needsAcknowledgement && waivableFindings.length > 0 && (
                    <div className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id="acknowledge-findings"
                        label="I acknowledge the conditional compatibility findings for this scene and profile."
                        checked={acknowledgeChecked}
                        disabled={acknowledging || !!acknowledgementId}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setAcknowledgeChecked(checked);
                          if (checked) {
                            onAcknowledge();
                          } else {
                            setAcknowledgementId('');
                          }
                        }}
                      />
                      <ul className="small text-muted mb-0 mt-2">
                        {waivableFindings.map((finding) => (
                          <li key={finding.id}>
                            {finding.code}: {finding.summary}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="form-stage">
            <Form.Label>3. Scene</Form.Label>
            <Form.Select
              value={sceneId}
              onChange={(e) => setSceneId(e.target.value)}
              required
              disabled={!projectId || loadingDeps}
            >
              <option value="">Select a scene…</option>
              {scenes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.display_name} — {s.asset_path}
                </option>
              ))}
            </Form.Select>
            {projectId && !loadingDeps && scenes.length === 0 && (
              <Alert variant="warning" className="mt-2 mb-0">
                No scenes on this project. Add one from the project detail page.
              </Alert>
            )}
          </div>

          <div className="form-stage">
            <Form.Label>4. Compatible bake profile</Form.Label>
            <Form.Select
              value={profileId}
              onChange={(e) => setProfileId(e.target.value)}
              required
              disabled={!projectId || loadingDeps}
            >
              <option value="">Select a bake profile…</option>
              {compatibleProfiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.is_system ? ' (system)' : ''}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="form-stage">
            <Form.Label>5. Review</Form.Label>
            <p className="text-muted mb-2">
              Submitting creates a queued BakeJob and pending BakeAttempt. It does not run Unity.
            </p>
            <Button type="submit" variant="primary" disabled={!canSubmit}>
              {submitting ? 'Submitting…' : 'Submit bake job'}
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
}

export default NewBakePage;
